/**
 * Creative Blueprint Slide
 *
 * Slide 6: Shows the creative blueprint with sections and duration.
 */

import { motion } from 'framer-motion'
import { FileText, Clock, Layers, CheckCircle } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface CreativeBlueprintSlideProps {
  artifacts: OnboardingArtifacts
}

export default function CreativeBlueprintSlide({ artifacts }: CreativeBlueprintSlideProps) {
  // Extract blueprint data from artifacts
  const blueprint = artifacts.blueprint as {
    sections?: Array<{
      name?: string
      start_sec?: number
      end_sec?: number
      objective?: string
      voiceover?: string
      overlay_text?: string
    }>
    total_duration_sec?: number
    strategy?: { source?: string }
    brand?: string
  } | null

  const sections = blueprint?.sections || []
  const totalDuration = blueprint?.total_duration_sec || 25
  const sectionCount = sections.length || 5

  // Transform sections to display format with duration
  const displaySections = sections.length > 0
    ? sections.map(section => ({
        name: section.name,
        duration: (section.end_sec || 0) - (section.start_sec || 0),
        description: section.objective || section.overlay_text,
      }))
    : [
        { name: 'Hook', duration: 3, description: 'Attention-grabbing opener' },
        { name: 'Problem', duration: 5, description: 'Pain point identification' },
        { name: 'Solution', duration: 10, description: 'Product demonstration' },
        { name: 'Proof', duration: 4, description: 'Testimonials & results' },
        { name: 'CTA', duration: 3, description: 'Call to action' },
      ]

  return (
    <div className="flex flex-col items-center text-center">
      {/* Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring' }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-8 shadow-lg"
      >
        <FileText className="w-10 h-10 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        Creative Blueprint
      </motion.h2>

      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-tf-text-muted mb-6"
      >
        Your ad structure optimized for conversions
      </motion.p>

      {/* Stats */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="flex items-center gap-6 mb-8"
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10">
          <Layers className="w-5 h-5 text-cyan-400" />
          <span className="text-lg font-semibold text-cyan-400">{sectionCount}</span>
          <span className="text-sm text-tf-text-muted">Sections</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10">
          <Clock className="w-5 h-5 text-blue-400" />
          <span className="text-lg font-semibold text-blue-400">{totalDuration}s</span>
          <span className="text-sm text-tf-text-muted">Duration</span>
        </div>
      </motion.div>

      {/* Sections timeline */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-tf-border" />

          {/* Sections */}
          {displaySections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="relative flex items-start gap-4 mb-4 last:mb-0"
            >
              {/* Timeline dot */}
              <div className="relative z-10 w-8 h-8 rounded-full bg-tf-bg-card border-2 border-cyan-400 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-cyan-400" />
              </div>

              {/* Content */}
              <div className="flex-1 text-left pb-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-tf-text">{section.name}</span>
                  <span className="text-xs text-tf-text-muted px-2 py-1 rounded bg-tf-bg-muted">
                    {section.duration}s
                  </span>
                </div>
                {section.description && (
                  <p className="text-sm text-tf-text-muted mt-1">{section.description}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Format badge */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tf-bg-muted border border-tf-border"
      >
        <span className="text-sm text-tf-text-muted">Format:</span>
        <span className="text-sm font-medium text-tf-text">Vertical Video (9:16)</span>
      </motion.div>
    </div>
  )
}
